import { css } from '@emotion/react'
import styled from '@emotion/styled'
import SettingsIcon from '@mui/icons-material/Settings'
import {
  Box,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Chip as MuiChip,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography as MuiTypography,
} from '@mui/material'
import { spacing } from '@mui/system'
import { rgba } from 'polished'
import React from 'react'

import useT from '@/i18n/useT'

const illustrationCardStyle = (props: any) => css`
  ${props.illustration &&
  props.theme.palette.mode !== 'dark' &&
  `
    background: ${rgba(props.theme.palette.primary.main, 0.125)};
    color: ${props.theme.palette.primary.main};
  `}
`

const Card = styled(MuiCard)<{ illustration?: string }>`
  position: relative;

  ${illustrationCardStyle}
`

const Typography = styled(MuiTypography)(spacing)

const CardContent = styled(MuiCardContent)`
  position: relative;

  &:last-child {
    padding-bottom: ${props => props.theme.spacing(4)};
  }
`

const Chip = styled(MuiChip)`
  position: absolute;
  top: 16px;
  right: 16px;
  height: 20px;
  padding: 4px 0;
  font-size: 85%;
  background-color: ${props => props.theme.palette.secondary.main};
  color: ${props => props.theme.palette.common.white};
  margin-bottom: ${props => props.theme.spacing(4)};

  span {
    padding-left: ${props => props.theme.spacing(2)};
    padding-right: ${props => props.theme.spacing(2)};
  }
`

const illustrationPercentageStyle = (props: any) => css`
  ${props.illustration &&
  props.theme.palette.mode !== 'dark' &&
  `
    color: ${rgba(props.theme.palette.primary.main, 0.85)};
  `}
`

const Percentage = styled(MuiTypography)<{
  percentagecolor: string
  illustration?: string
}>`
  span {
    color: ${props => props.percentagecolor};
    background: ${props => rgba(props.percentagecolor, 0.1)};
    padding: 2px;
    border-radius: 3px;
    margin-right: ${props => props.theme.spacing(2)};
  }

  ${illustrationPercentageStyle}
`

const IllustrationImage = styled.img`
  height: 100px;
  position: absolute;
  right: ${props => props.theme.spacing(1)};
  bottom: ${props => props.theme.spacing(1)};
  display: none;

  ${props => props.theme.breakpoints.between('xs', 'lg')} {
    display: block;
  }

  @media (min-width: 1600px) {
    display: block;
  }
`

type StatsProps = {
  title: string
  amount: string | React.ReactNode
  chip?: string
  chipTooltip?: string
  changeTooltip?: string
  change: {
    text: string
    color: string
  } | null
  changeTitle?: string
  illustration?: string
  loading?: boolean
  onClick?: () => void
  onIconClick?: () => void
  Icon?: React.ElementType
}

const BottomWrapper = styled(Box)`
  min-height: 20px;
  margin-top: -${props => props.theme.spacing(2)};
`

const Stats: React.FC<StatsProps> = ({
  title,
  amount,
  chip,
  chipTooltip,
  change,
  changeTitle,
  illustration,
  loading,
  onClick,
  onIconClick,
  changeTooltip,
  Icon = SettingsIcon,
}) => {
  const { t } = useT()
  return (
    <Card
      illustration={illustration}
      sx={{
        flex: 1,
        height: '100%',
        ...(onClick
          ? {
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'action.hover' },
            }
          : {}),
      }}
      onClick={onClick}
    >
      <CardContent>
        {loading ? (
          <>
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </>
        ) : (
          <>
            <Stack direction={'row'} justifyContent="space-between">
              <Typography variant="h6" mb={4} sx={{ pr: chip ? 10 : 0 }}>
                {title}
              </Typography>
              {onIconClick && (
                <IconButton onClick={onIconClick}>
                  <Icon />
                </IconButton>
              )}
            </Stack>
            {React.isValidElement(amount) ? (
              <Box sx={{ minHeight: '3rem' }} mb={4}>
                {amount}
              </Box>
            ) : (
              <Typography variant="h3" mb={4} sx={{ minHeight: '3rem' }}>
                <Box fontWeight="bold">{amount}</Box>
              </Typography>
            )}
            <BottomWrapper>
              {!!change && (
                <Tooltip title={changeTooltip || ''} arrow>
                  <Percentage
                    variant="subtitle2"
                    color="textSecondary"
                    percentagecolor={change.color}
                    illustration={illustration}
                  >
                    <span>{change.text}</span> {changeTitle}
                  </Percentage>
                </Tooltip>
              )}
            </BottomWrapper>

            {!illustration && !!chip && (
              <Tooltip title={chipTooltip || ''} arrow>
                <Chip label={chip} />
              </Tooltip>
            )}
          </>
        )}
      </CardContent>
      {!!illustration && (
        <IllustrationImage
          src={illustration}
          alt={t('stats.illustration.alt')}
        />
      )}
    </Card>
  )
}

export default Stats
